import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControlComponent,
  PrimengButtonDirective,
  PrimengInputTextDirective,
  PrimengSelectDirective,
  SelectItem,
  UrlValidator,
} from '@web/shared/ui';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DatePicker } from 'primeng/datepicker';
import {
  AdminMultimediaImageUpload,
  MultimediaChooseOrUploadControlComponent,
} from '@web/shared/module/multimedia';
import { executeOrNull, Nillable, valueOrDefault } from '@web/utils';
import { Observable } from 'rxjs';
import { Select } from 'primeng/select';
import { Button } from 'primeng/button';
import { AdminBrandBannerStatus } from '@web/shared/module/brand';
import {
  AdminBrandBannerDetailsForm,
  AdminBrandBannerDetailsFormGroup,
} from '../../models/admin-brand-banner-details-form';
import { AdminBrandBannerStatusProviderService } from '../../providers/admin-brand-banner-status-provider.service';
import { InputText } from 'primeng/inputtext';

@Component({
  selector: 'app-admin-brand-banner-details-form',
  imports: [
    Button,
    CommonModule,
    DatePicker,
    FormControlComponent,
    MultimediaChooseOrUploadControlComponent,
    PrimengButtonDirective,
    PrimengInputTextDirective,
    PrimengSelectDirective,
    ReactiveFormsModule,
    Select,
    InputText,
  ],
  templateUrl: './admin-brand-banner-details-form.component.html',
  styleUrl: './admin-brand-banner-details-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminBrandBannerDetailsFormComponent implements OnInit {
  private readonly builder = inject(FormBuilder);
  private readonly statusProvider = inject(
    AdminBrandBannerStatusProviderService,
  );

  public readonly data = input<Nillable<AdminBrandBannerDetailsForm>>();
  public readonly submitClickEvent = output<AdminBrandBannerDetailsForm>();

  protected readonly form = this.buildForm();
  protected readonly statusSelectItems$ = this.statusSelectItemsSource();
  protected readonly valueOrDefault = valueOrDefault;

  public ngOnInit(): void {
    this.fillByData();
  }

  public handleSubmitClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  private toData(): AdminBrandBannerDetailsForm {
    const data = this.form.getRawValue();

    return new AdminBrandBannerDetailsForm({
      fileId: data.file!.id,
      smallFileId: data.smallFile?.id,
      status: data.status!,
      url: data.url,
      visibilityEndDate: data.visibilityEndDate,
      visibilityStartDate: data.visibilityStartDate,
    });
  }

  private buildForm(): AdminBrandBannerDetailsFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      url: this.builder.control<Nillable<string>>(null, [UrlValidator.url]),
      file: this.builder.control<Nillable<AdminMultimediaImageUpload>>(null, [Validators.required]),
      smallFile: this.builder.control<Nillable<AdminMultimediaImageUpload>>(null),
      status: this.builder.control<Nillable<AdminBrandBannerStatus>>(null, [Validators.required]),
      visibilityStartDate: this.builder.control<Nillable<Date>>(null),
      visibilityEndDate: this.builder.control<Nillable<Date>>(null),
    });
  }

  private fillByData(): void {
    const data = this.data();

    /* prettier-ignore */
    this.form.patchValue({
      file: executeOrNull(data?.fileId, (id) => new AdminMultimediaImageUpload({id})),
      smallFile: executeOrNull(data?.smallFileId, (id) => new AdminMultimediaImageUpload({id})),
      status: data?.status,
      url: data?.url,
      visibilityEndDate: data?.visibilityEndDate,
      visibilityStartDate: data?.visibilityStartDate,
    });
  }

  private statusSelectItemsSource(): Observable<
    SelectItem<AdminBrandBannerStatus>[]
  > {
    return this.statusProvider.selectItems();
  }
}
